import { useMap } from "react-leaflet";
import { MapObject } from "../ui/map-view";
import { useEffect, useRef } from "react";
import L from "leaflet";
import type { LatLngExpression } from "leaflet";

const toLatLng = (p: LatLngExpression): [number, number] | null => {
  if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
  const o = p as { lat?: number; lng?: number };
  return o.lat == null || o.lng == null ? null : [o.lat, o.lng];
};

const DESELECT_SETTLE_MS = 300;

// Automatically fit bounds to show all markers
export const AutoFitBounds: React.FC<{
  objects?: MapObject[];
  trucks?: MapObject[];
  depots?: MapObject[];
  /** Drawn route segments, so the fit contains the track and not just the pins. */
  routePaths?: LatLngExpression[][];
  infoId: string | null;
  /** Pixels a bar over the map's bottom edge hides, e.g. the playback player. */
  bottomInset?: number;
  /** A new value fits the same items again, e.g. after zooming in on one of them. */
  refitKey?: string | number;
  /** More points the fit takes in, such as the day's job sites; only their arrival refits. */
  fitPoints?: { lat: number; lon: number }[];
  /** Clearing the selection returns to the fit of everything (the default view). */
  refitOnDeselect?: boolean;
}> = ({
  objects,
  trucks,
  depots,
  routePaths,
  infoId,
  bottomInset = 0,
  refitKey,
  fitPoints,
  refitOnDeselect = false,
}) => {
  const map = useMap();
  const hasInitialized = useRef(false);
  const fittedTo = useRef<string | null>(null);
  const pendingFit = useRef<(() => void) | null>(null);
  const hadSelection = useRef(false);
  const deselectFit = useRef<number | null>(null);
  const fitPointsSeen = useRef(false);

  useEffect(() => {
    // A cancelled fit never happened, so the same items must fit again when they return.
    const cancelDeselectFit = () => {
      if (deselectFit.current === null) return;
      window.clearTimeout(deselectFit.current);
      deselectFit.current = null;
      fittedTo.current = null;
    };
    const routePoints = (routePaths || [])
      .flat()
      .map(toLatLng)
      .filter((p): p is [number, number] => p !== null);

    // A drawn route is the subject of the map, so it is fitted even though
    // selecting it sets infoId.
    if (infoId) cancelDeselectFit();
    if (infoId && routePoints.length === 0) {
      hadSelection.current = true;
      return;
    }
    let settle = deselectFit.current !== null;
    if (infoId) hadSelection.current = true;
    else if (hadSelection.current) {
      hadSelection.current = false;
      if (refitOnDeselect) {
        fittedTo.current = null;
        settle = true;
      }
    }

    const markers = [...(objects || []), ...(trucks || []), ...(depots || [])];
    const extra = routePoints.length ? [] : (fitPoints ?? []);
    const points: [number, number][] = [
      ...markers.map((m) => [m.lat, m.lon] as [number, number]),
      ...extra.map((p) => [p.lat, p.lon] as [number, number]),
      ...routePoints,
    ].filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));

    if (points.length === 0 || (points.length === 1 && routePoints.length === 0)) {
      cancelDeselectFit();
      return;
    }

    // Keyed on which items are shown, not where they are: position polling
    // rebuilds these arrays every few seconds, and refitting on that dragged
    // the view back out from under anyone panning or zooming. The truck moves
    // with playback, so its coordinates must stay out of the key too. The
    // route counts by its start, not its length or end: a live route's track
    // grows with every refetch, and refitting on that does the same.
    const start = routePoints.length ? routePoints[0].join(",") : "";
    // Their first arrival refits; a scope change emptying them for a moment doesn't.
    if (extra.length > 0) fitPointsSeen.current = true;
    const key = [markers.map((m) => m.id).join(","), start, refitKey, fitPointsSeen.current].join(
      "|",
    );
    if (fittedTo.current === key) return;
    fittedTo.current = key;

    const fit = () => {
      pendingFit.current = null;
      map.fitBounds(L.latLngBounds(points), {
        paddingTopLeft: [24, 24],
        paddingBottomRight: [24, 24 + bottomInset],
        animate: hasInitialized.current,
        maxZoom: 16,
      });
      hasInitialized.current = true;
    };
    // Leaflet drops a zoom asked for mid zoom-animation, and a route's pieces
    // arrive in quick succession: its final fit was lost to the previous one.
    const run = () => {
      if (pendingFit.current) map.off("zoomend", pendingFit.current);
      if ((map as unknown as { _animatingZoom?: boolean })._animatingZoom) {
        pendingFit.current = fit;
        map.once("zoomend", fit);
      } else {
        fit();
      }
    };
    // Hidden trucks come back over several renders: one fit once they have, not one per render.
    if (settle) {
      // Rescheduled, not cancelled: `fittedTo` must keep the key set above.
      if (deselectFit.current !== null) window.clearTimeout(deselectFit.current);
      deselectFit.current = window.setTimeout(() => {
        deselectFit.current = null;
        run();
      }, DESELECT_SETTLE_MS);
    } else {
      run();
    }
  }, [
    objects,
    trucks,
    depots,
    routePaths,
    infoId,
    bottomInset,
    refitKey,
    fitPoints,
    refitOnDeselect,
    map,
  ]);

  useEffect(
    () => () => {
      if (pendingFit.current) map.off("zoomend", pendingFit.current);
      if (deselectFit.current !== null) window.clearTimeout(deselectFit.current);
    },
    [map],
  );

  return null;
};
